import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Flex, Checkbox, Alert, Spin } from "antd"
import PdfViewer from "ui/PdfViewer"
import { ClockCircleOutlined } from "@ant-design/icons"
import SignatureCanvas from "react-signature-canvas"
import styles from "./index.module.css"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { useTranslation } from "react-i18next"
import { updateWaiverSubmission, getWaiverSubmissionsByTripBooking, saveWaiverAsDraft } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { Text } from "ui/Typography"

export const SignDocumentModal = ({
  totalDocumentCount,
  signedDocumentsCount,
  uuid,
  waiverSubmissionId,
  pendingSubmission,
  waiver,
  open,
  onCancel,
  onDraftSaved,
  onNext,
  bookingWaivers,
  fullScreen,
  showPageHeader
}) => {
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const logoSrc = theme === themes.sportswoman ? bgWoman : bgMan
  const { t } = useTranslation()
  const { loading: submitting } = useSelector((state) => state.digitalWaiver)
  // Snapshot on mount so live redux updates don't shift the counter mid-session
  const initialSignedCount = useRef(signedDocumentsCount ?? waiver?.signed_document_count ?? 0)
  const [isChecked, setIsChecked] = useState(false)
  const [fileList, setFileList] = useState([])
  const [pdfUrls, setPdfUrls] = useState([])
  const [currentWaiverIndex, setCurrentWaiverIndex] = useState(0)
  const [signatureDataUrl, setSignatureDataUrl] = useState(null)
  const [documentStates, setDocumentStates] = useState([]) // Store checkbox and signature for each document
  const [signatureDisabled, setSignatureDisabled] = useState(false) // Control signature canvas interaction
  const [isNavigating, setIsNavigating] = useState(false) // Track when user is navigating between documents
  const [pdfLoading, setPdfLoading] = useState(false)
  const [showDraftConfirmModal, setShowDraftConfirmModal] = useState(false)
  const [draftSubmitting, setDraftSubmitting] = useState(false)
  const signCanvas = useRef(null)

  let pendingDocs = null
  if (pendingSubmission?.length > 0) {
    pendingDocs = pendingSubmission
  } else if (bookingWaivers?.length > 0) {
    pendingDocs = bookingWaivers
  }
  const handleSignatureEnd = () => {
    // Prevent signature if document is submitted
    if (signatureDisabled) return

    const pad = signCanvas.current
    if (!pad || typeof pad.isEmpty !== "function" || pad.isEmpty() || typeof pad.getTrimmedCanvas !== "function") {
      setSignatureDataUrl(null)
      // Update document states
      setDocumentStates((prev) => {
        const newStates = [...prev]
        if (newStates[currentWaiverIndex]) {
          newStates[currentWaiverIndex] = {
            ...newStates[currentWaiverIndex],
            signatureDataUrl: null
          }
        }
        return newStates
      })
      return
    }
    const dataUrl = pad.toDataURL()
    setSignatureDataUrl(dataUrl)
    // Update document states
    setDocumentStates((prev) => {
      const newStates = [...prev]
      if (newStates[currentWaiverIndex]) {
        newStates[currentWaiverIndex] = {
          ...newStates[currentWaiverIndex],
          signatureDataUrl: dataUrl
        }
      }
      return newStates
    })
  }

  useEffect(() => {
    const waiverDocs = waiver?.pending_submissions?.length > 0 ? waiver.pending_submissions : waiver?.waivers
    const waivers = pendingDocs || waiverDocs || []
    if (waivers.length > 0) {
      const fileObjects = waivers.map((waiverItem, index) => {
        const filename = waiverItem?.document_filename || waiverItem?.document_url?.split("/").pop() || `Document ${index + 1}`
        return {
          uid: `existing-${Date.now()}-${index}`,
          name: decodeURIComponent(filename),
          url: waiverItem.document_url,
          status: "done"
        }
      })
      setFileList(fileObjects)

      let createdUrls = []
      const fetchDocuments = async () => {
        setPdfLoading(true)
        try {
          const blobUrls = await Promise.all(
            waivers.map(async (w) => {
              const response = await fetch(w.document_url)
              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
              const blob = await response.blob()
              return URL.createObjectURL(blob)
            })
          )
          createdUrls = blobUrls
          setPdfUrls(blobUrls)
        } catch (error) {
          console.error("Error fetching documents:", error)
          setPdfUrls(waivers.map((w) => w.document_url))
        } finally {
          setPdfLoading(false)
        }
      }
      fetchDocuments()

      return () => {
        createdUrls.forEach((url) => URL.revokeObjectURL(url))
      }
    }
  }, [waiver?.waivers, waiver?.pending_submissions, bookingWaivers, pendingSubmission])

  // Initialize document states when waivers are loaded
  useEffect(() => {
    if (pdfUrls.length > 0) {
      const newStates = Array(pdfUrls.length)
        .fill(null)
        .map(() => ({
          isChecked: false,
          signatureDataUrl: null,
          submitted: false
        }))
      setDocumentStates(newStates)
    }
  }, [pdfUrls.length])

  // Update current checkbox and signature when index changes
  useEffect(() => {
    if (documentStates[currentWaiverIndex]) {
      setIsChecked(documentStates[currentWaiverIndex].isChecked)

      // Only restore signature if document is submitted
      if (documentStates[currentWaiverIndex]?.submitted) {
        setSignatureDataUrl(documentStates[currentWaiverIndex].signatureDataUrl)
      } else if (isNavigating) {
        // Clear signature only when navigating to unsigned documents
        setSignatureDataUrl(null)
        setIsNavigating(false) // Reset navigation flag
      }

      // Only disable signature if document is already submitted
      const shouldDisable = documentStates[currentWaiverIndex]?.submitted
      setSignatureDisabled(shouldDisable)
    }
  }, [currentWaiverIndex, documentStates])

  // Restore signature on canvas when signatureDataUrl changes
  useEffect(() => {
    if (signCanvas.current) {
      const canvas = signCanvas.current
      // Clear canvas first to prevent overlapping signatures
      if (typeof canvas.clear === "function") {
        canvas.clear()
      }
      // Only restore if there's signature data
      if (signatureDataUrl && typeof canvas.fromDataURL === "function") {
        canvas.fromDataURL(signatureDataUrl)
      }

      // Apply disabled state to canvas element
      const canvasElement = canvas.getCanvas ? canvas.getCanvas() : null
      if (canvasElement) {
        if (signatureDisabled) {
          canvasElement.style.pointerEvents = "none"
          canvasElement.style.opacity = "0.5"
          canvasElement.style.cursor = "not-allowed"
        } else {
          canvasElement.style.pointerEvents = "auto"
          canvasElement.style.opacity = "1"
          canvasElement.style.cursor = "crosshair"
        }
      }
    }
  }, [signatureDataUrl, signatureDisabled])

  const clearSignature = () => {
    if (signCanvas.current && typeof signCanvas.current.clear === "function") {
      signCanvas.current.clear()
    }
    setSignatureDataUrl(null)
    // Update document states
    setDocumentStates((prev) => {
      const newStates = [...prev]
      if (newStates[currentWaiverIndex]) {
        newStates[currentWaiverIndex] = {
          ...newStates[currentWaiverIndex],
          signatureDataUrl: null
        }
      }
      return newStates
    })
  }
  const handleSaveDraft = () => {
    setShowDraftConfirmModal(true)
  }

  const handleDraftConfirmOk = async () => {
    setDraftSubmitting(true)
    try {
      await dispatch(saveWaiverAsDraft(waiverSubmissionId))
      setShowDraftConfirmModal(false)
      if (onDraftSaved) {
        onDraftSaved()
      } else {
        onCancel()
      }
    } finally {
      setDraftSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    // Check if current document has already been submitted
    if (documentStates[currentWaiverIndex]?.submitted) {
      // Already submitted, just move to next document
      if (currentWaiverIndex < pdfUrls.length - 1) {
        const nextIndex = currentWaiverIndex + 1
        setCurrentWaiverIndex(nextIndex)
        // Check if next document is submitted to determine if signature should be disabled
        if (documentStates[nextIndex]?.submitted) {
          setSignatureDisabled(true)
        } else {
          setSignatureDisabled(false)
          setIsNavigating(true) // Set navigation flag
          // Clear signature when moving to unsigned next document
          setSignatureDataUrl(null)
        }
      } else {
        // This was the last document, submission is complete
        if (onNext) {
          onNext()
        }
      }
      return
    }

    const formData = new FormData()

    // Always send the current document's state
    const waiverDocs = waiver?.pending_submissions?.length > 0 ? waiver.pending_submissions : waiver?.waivers

    if (pendingDocs || waiverDocs) {
      const waivers = pendingDocs || waiverDocs || []
      const currentWaiverItem = waivers[currentWaiverIndex]
      if (currentWaiverItem) {
        formData.append(
          `waiver_submission[waiver_submission_documents_attributes][0][booking_waiver_id]`,
          currentWaiverItem.booking_waiver_id
        )

        // Add current document's signature if available
        if (documentStates[currentWaiverIndex]?.signatureDataUrl) {
          formData.append(
            `waiver_submission[waiver_submission_documents_attributes][0][signature]`,
            documentStates[currentWaiverIndex].signatureDataUrl
          )
        }
      }
    }

    // Send API call for current document - navigate to next only on success
    dispatch(updateWaiverSubmission(waiverSubmissionId, formData)).then((response) => {
      toaster.success(response?.message)

      // Mark current document as submitted
      setDocumentStates((prev) => {
        const newStates = [...prev]
        if (newStates[currentWaiverIndex]) {
          newStates[currentWaiverIndex] = {
            ...newStates[currentWaiverIndex],
            submitted: true
          }
        }
        return newStates
      })

      // Always refresh trip booking waiver submissions after each document
      if (uuid) {
        dispatch(getWaiverSubmissionsByTripBooking(uuid))
      }

      // Navigate to next document on success
      if (currentWaiverIndex < pdfUrls.length - 1) {
        const nextIndex = currentWaiverIndex + 1
        setCurrentWaiverIndex(nextIndex)
        setSignatureDisabled(false)
        setIsNavigating(true)
        setSignatureDataUrl(null)
      } else if (onNext) {
        onNext(uuid)
      } else if (onCancel) {
        onCancel(uuid)
      }
    })
  }

  const content = (
    <Spin spinning={pdfLoading} tip="Loading document...">
      {/* Mandatory signing notice */}
      {!signatureDisabled && (
        <Alert
          type="warning"
          showIcon
          message="Signature required"
          description="This waiver is mandatory. Please read the document carefully and sign it before your trip."
          className={styles.alertWarning}
        />
      )}

      {/* Document Preview Section */}
      <div className={styles.previewSection}>
        <div className={styles.title}>Document Preview</div>
        {pdfUrls.length > 0 && (
          <div className={styles.pdfPreviewContainer}>
            <div className={styles.pdfViewer}>
              <Flex align="center" justify="end">
                {initialSignedCount.current + currentWaiverIndex + 1} of {totalDocumentCount ?? waiver?.document_count ?? pdfUrls.length}{" "}
                waivers
              </Flex>
              <div className={`mb-xs ${styles.documentTitle}`}>
                Document {initialSignedCount.current + currentWaiverIndex + 1}:{" "}
                {fileList[currentWaiverIndex]?.name || `Document ${initialSignedCount.current + currentWaiverIndex + 1}`}
              </div>
              {pdfUrls[currentWaiverIndex] ? (
                <PdfViewer url={pdfUrls[currentWaiverIndex]} height="calc(100% - 80px)" />
              ) : (
                <div className={styles.noDocumentSpin}>
                  <Spin size="small" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Legal Text & Checkbox */}
        <div className={`mt-xs ${styles.legalSection}`}>
          <div className={styles.legalText}>
            <Checkbox
              checked={isChecked}
              disabled={signatureDisabled}
              onChange={(e) => {
                const newChecked = e.target.checked
                setIsChecked(newChecked)
                setDocumentStates((prev) => {
                  const newStates = [...prev]
                  if (newStates[currentWaiverIndex]) {
                    newStates[currentWaiverIndex] = {
                      ...newStates[currentWaiverIndex],
                      isChecked: newChecked
                    }
                  }
                  return newStates
                })
              }}
              className={styles.checkboxTop}
            >
              {t("digital_waiver_and_contracts.leagal_documents")}
            </Checkbox>
            <Text className="mt-s">{t("digital_waiver_and_contracts.legal_document_desc")}</Text>
          </div>

          {/* Signature Section - Custom Styled */}
          {isChecked && (
            <div className={`mt-m relative ${styles.canvasLayer}`}>
              <div className={styles.title}>{t("digital_waiver_and_contracts.signature")}</div>
              <div className={`${styles.signatureCanvasWrapper} ${signatureDisabled ? styles.disabledCanvas : ""}`}>
                {/* Actual Canvas Layer */}
                <SignatureCanvas ref={signCanvas} penColor="black" onEnd={handleSignatureEnd} />
                <div className={styles.signaturePlaceholderText}>{t("digital_waiver_and_contracts.draw_signature")}</div>
                {/* Disabled overlay */}
                {signatureDisabled && <div className={styles.disabledOverlay} />}
              </div>
              <Button
                type="text"
                size="small"
                onClick={clearSignature}
                className={styles.clearButton}
                icon={<span className={styles.clearButtonIcon}>×</span>}
                disabled={signatureDisabled}
              ></Button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Flex justify="flex-end" align="center">
        <Flex gap={12}>
          <Button color="primary" variant="outlined" onClick={handleSaveDraft} disabled={submitting || (isChecked && !!signatureDataUrl)}>
            Save Draft & Sign Later
          </Button>
          {waiver?.pending_submissions?.length === 1 || waiver?.waivers?.length === 1 ? null : (
            <Button
              color="primary"
              variant="outlined"
              onClick={() => {
                if (currentWaiverIndex > 0) {
                  const prevIndex = currentWaiverIndex - 1
                  setIsNavigating(true) // Set navigation flag
                  setCurrentWaiverIndex(prevIndex)
                }
              }}
              disabled={currentWaiverIndex === 0}
            >
              {t("embedded.booking_confirmation.previous")}
            </Button>
          )}

          <Button
            color="primary"
            variant="solid"
            loading={submitting}
            onClick={handleSubmit}
            disabled={!isChecked || !signatureDataUrl || submitting}
          >
            {currentWaiverIndex < pdfUrls.length - 1 ? t("embedded.booking_confirmation.next") : "Submit"}
          </Button>
        </Flex>
      </Flex>
    </Spin>
  )

  const draftConfirmModal = (
    <Modal
      open={showDraftConfirmModal}
      onCancel={() => setShowDraftConfirmModal(false)}
      footer={null}
      centered
      width={460}
      maskClosable={false}
    >
      <Flex vertical align="center" gap={24} className={styles.draftModalBody}>
        <ClockCircleOutlined className={styles.draftModalIcon} />
        <Flex vertical align="center" gap={8}>
          <span className={styles.draftModalTitle}>Save Draft & Sign Later?</span>
          <span className={styles.draftModalDesc}>
            Your progress will be saved as a draft. You can come back and finish signing the remaining documents whenever you&apos;re ready.
          </span>
          <span className={styles.draftModalNote}>
            To resume, you will receive a link via your email or phone number — you can continue signing from that link.
          </span>
        </Flex>
        <Flex gap={12} className={styles.draftModalButtons}>
          <Button block onClick={() => setShowDraftConfirmModal(false)} disabled={draftSubmitting}>
            Cancel
          </Button>
          <Button type="primary" block loading={draftSubmitting} onClick={handleDraftConfirmOk}>
            Ok
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )

  if (fullScreen) {
    return (
      <>
        <div className={styles.fullScreenWrapper}>
          {showPageHeader && (
            <div className={styles.fullScreenHeader}>
              <img src={logoSrc} alt="Digital Sportsman" className={styles.fullScreenHeaderLogo} />
            </div>
          )}
          <div className={styles.fullScreenInner}>
            <div className={styles.fullScreenTitle}>Sign the document</div>
            <div className={styles.fullScreenContent}>{content}</div>
          </div>
        </div>
        {draftConfirmModal}
      </>
    )
  }

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        centered
        width={1100}
        maskClosable={false}
        title={<span className={styles.modalTitleText}>Sign the document</span>}
      >
        {content}
      </Modal>
      {draftConfirmModal}
    </>
  )
}

SignDocumentModal.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  onNext: PropTypes.func,
  onDraftSaved: PropTypes.func,
  fullScreen: PropTypes.bool,
  showPageHeader: PropTypes.bool,
  totalDocumentCount: PropTypes.number,
  signedDocumentsCount: PropTypes.number,
  uuid: PropTypes.string,
  waiverSubmissionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pendingSubmission: PropTypes.arrayOf(
    PropTypes.shape({
      booking_waiver_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      document_url: PropTypes.string,
      document_filename: PropTypes.string
    })
  ),
  bookingWaivers: PropTypes.arrayOf(
    PropTypes.shape({
      booking_waiver_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      document_url: PropTypes.string,
      document_filename: PropTypes.string
    })
  ),
  waiver: PropTypes.shape({
    waivers: PropTypes.array,
    pending_submissions: PropTypes.array,
    signed_document_count: PropTypes.number,
    document_count: PropTypes.number
  })
}
